import React, { useState } from 'react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { ScrollArea } from '../../ui/scroll-area';
import { Separator } from '../../ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../ui/tabs';
import { 
  Target, Flag, Plus, CheckCircle, Circle, Clock, 
  Loader2, Trash2, X, Check, Calendar, FileText, ShieldCheck, Rocket,
  FileCode, TestTube, ListChecks, AlertTriangle, ExternalLink, Link, Key, Settings, Lock, ClipboardList,
  Layout, Smartphone, Accessibility, ArrowRight, XCircle, RefreshCw, Timer, Bug, Layers
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../ui/collapsible';

// Helper function to format date from YYYY-MM-DD string without timezone issues
const formatDateShort = (dateStr) => {
  if (!dateStr) return '—';
  // Parse the date string directly to avoid timezone issues
  const parts = dateStr.split('-');
  if (parts.length !== 3) return '—';
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthIndex = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);
  if (monthIndex < 0 || monthIndex > 11 || isNaN(day)) return '—';
  return `${months[monthIndex]} ${day}`;
};

// Helper to check if date is in the past
const isDatePast = (dateStr) => {
  if (!dateStr) return false;
  const parts = dateStr.split('-');
  if (parts.length !== 3) return false;
  const dateValue = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return dateValue < today;
};

export const EnhancedScopeModal = ({
  open,
  onOpenChange,
  scopeData,
  scopeLoading,
  canEdit,
  selectedItem,
  pendingProgress,
  onSelectItem,
  onSetPendingProgress,
  onUpdateItem,
  onDeleteItem,
  onAddMvpItem,
  onAddFutureItem,
}) => {
  const handleClose = (isOpen) => {
    if (!isOpen) {
      onSelectItem(null);
      onSetPendingProgress(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Target className="h-5 w-5 text-indigo-600" />
            RHINO-1 Project Scope
          </DialogTitle>
          <DialogDescription>MVP deliverables and future features - Click any item to edit</DialogDescription>
        </DialogHeader>
        
        {scopeLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="flex gap-4">
            {/* Items List */}
            <div className={`${selectedItem ? 'w-1/2' : 'w-full'} space-y-6`}>
              {/* Metrics Summary */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-green-600">{scopeData.metrics?.completed || 0}</p>
                  <p className="text-[10px] text-green-700 dark:text-green-300">Completed</p>
                </div>
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-blue-600">{scopeData.metrics?.in_progress || 0}</p>
                  <p className="text-[10px] text-blue-700 dark:text-blue-300">In Progress</p>
                </div>
                <div className="p-3 bg-slate-100 dark:bg-slate-800 rounded-lg text-center">
                  <p className="text-xl font-bold text-slate-600">{scopeData.metrics?.pending || 0}</p>
                  <p className="text-[10px] text-slate-500">Pending</p>
                </div>
                <div className="p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg text-center">
                  <p className="text-xl font-bold text-indigo-600">{scopeData.metrics?.total || 0}</p>
                  <p className="text-[10px] text-indigo-700 dark:text-indigo-300">Total</p>
                </div>
              </div>

              {/* MVP Items */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Flag className="h-4 w-4 text-orange-500" />
                    MVP Deliverables
                    <Badge variant="secondary" className="text-xs">{scopeData.metrics?.mvp_completed || 0}/{scopeData.metrics?.mvp_total || 0}</Badge>
                  </h3>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={onAddMvpItem}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[280px]">
                  <div className="space-y-2 pr-2">
                    {scopeData.mvp_items?.length > 0 ? (
                      scopeData.mvp_items.map(item => (
                        <div 
                          key={item.id} 
                          onClick={() => {
                            onSelectItem(item);
                            onSetPendingProgress(null);
                          }}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            selectedItem?.id === item.id 
                              ? 'bg-indigo-100 dark:bg-indigo-900/30 border border-indigo-300' 
                              : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                          }`}
                        >
                          {item.status === 'completed' ? (
                            <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                          ) : item.status === 'in_progress' ? (
                            <Clock className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          ) : (
                            <Circle className="h-4 w-4 text-slate-400 flex-shrink-0" />
                          )}
                          <span className={`text-sm font-medium flex-1 min-w-0 truncate ${item.status === 'completed' ? 'line-through text-muted-foreground' : ''}`}>
                            {item.name}
                          </span>
                          {/* Start Date */}
                          <div className="text-center flex-shrink-0 w-16">
                            <div className="text-[9px] text-muted-foreground uppercase">Start</div>
                            <div className="text-[11px] font-medium">
                              {formatDateShort(item.start_date)}
                            </div>
                          </div>
                          {/* Target Date */}
                          <div className="text-center flex-shrink-0 w-16">
                            <div className="text-[9px] text-muted-foreground uppercase">Target</div>
                            <div className={`text-[11px] font-medium ${isDatePast(item.target_date) && item.status !== 'completed' ? 'text-red-500' : ''}`}>
                              {formatDateShort(item.target_date)}
                            </div>
                          </div>
                          {/* Status Badge */}
                          <Badge 
                            variant={item.status === 'completed' ? 'default' : item.status === 'in_progress' ? 'secondary' : 'outline'}
                            className={`text-[10px] w-20 justify-center flex-shrink-0 ${
                              item.status === 'completed' ? 'bg-green-600' : 
                              item.status === 'in_progress' ? 'bg-blue-600 text-white' : ''
                            }`}
                          >
                            {item.status === 'completed' ? 'Done' : item.status === 'in_progress' ? 'In Progress' : 'Pending'}
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No MVP items defined yet</p>
                    )}
                  </div>
                </ScrollArea>
              </div>

              <Separator />

              {/* Future Features */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-sm flex items-center gap-2">
                    <Rocket className="h-4 w-4 text-purple-500" />
                    Future Features
                    <Badge variant="secondary" className="text-xs">{scopeData.future_items?.length || 0}</Badge>
                  </h3>
                  {canEdit && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs"
                      onClick={onAddFutureItem}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                  )}
                </div>
                <ScrollArea className="h-[180px]">
                  <div className="space-y-2 pr-2">
                    {scopeData.future_items?.length > 0 ? (
                      scopeData.future_items.map(item => (
                        <div 
                          key={item.id}
                          onClick={() => {
                            onSelectItem(item);
                            onSetPendingProgress(null);
                          }}
                          className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                            selectedItem?.id === item.id 
                              ? 'bg-purple-100 dark:bg-purple-900/30 border border-purple-300' 
                              : 'bg-slate-50 dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700'
                          }`}
                        >
                          <Circle className="h-4 w-4 text-purple-400 flex-shrink-0" />
                          <span className="text-sm font-medium flex-1 min-w-0 truncate">{item.name}</span>
                          {/* Start Date */}
                          <div className="text-center flex-shrink-0 w-16">
                            <div className="text-[9px] text-muted-foreground uppercase">Start</div>
                            <div className="text-[11px] font-medium">
                              {formatDateShort(item.start_date)}
                            </div>
                          </div>
                          {/* Target Date */}
                          <div className="text-center flex-shrink-0 w-16">
                            <div className="text-[9px] text-muted-foreground uppercase">Target</div>
                            <div className="text-[11px] font-medium">
                              {formatDateShort(item.target_date)}
                            </div>
                          </div>
                          {item.hub && <Badge variant="outline" className="text-[10px] flex-shrink-0">{item.hub}</Badge>}
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">No future features defined yet</p>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>
            
            {/* Detail Panel - Comprehensive Feature View */}
            {selectedItem && (
              <div className="w-1/2 border-l border-slate-200 dark:border-slate-700 pl-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-slate-800 dark:text-slate-200 truncate">{selectedItem.name}</h4>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge variant={selectedItem.status === 'completed' ? 'default' : selectedItem.status === 'in_progress' ? 'secondary' : 'outline'} className="text-[10px]">
                        {selectedItem.status || 'pending'}
                      </Badge>
                      <Badge variant="outline" className="text-[10px]">{selectedItem.category?.toUpperCase() || 'MVP'}</Badge>
                      {selectedItem.hub && <Badge variant="outline" className="text-[10px]">{selectedItem.hub}</Badge>}
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => onSelectItem(null)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                
                <Tabs defaultValue="overview" className="h-[calc(100%-60px)]">
                  <TabsList className="grid w-full grid-cols-8 h-8">
                    <TabsTrigger value="overview" className="text-xs">Overview</TabsTrigger>
                    <TabsTrigger value="technical" className="text-xs">Technical</TabsTrigger>
                    <TabsTrigger value="testing" className="text-xs">Testing</TabsTrigger>
                    <TabsTrigger value="setup" className="text-xs">Setup</TabsTrigger>
                    <TabsTrigger value="security" className="text-xs">Security</TabsTrigger>
                    <TabsTrigger value="uiux" className="text-xs">UI/UX</TabsTrigger>
                    <TabsTrigger value="errors" className="text-xs">Errors</TabsTrigger>
                    <TabsTrigger value="schedule" className="text-xs">Schedule</TabsTrigger>
                  </TabsList>
                  
                  <ScrollArea className="h-[420px] mt-2">
                    {/* Overview Tab */}
                    <TabsContent value="overview" className="mt-0 space-y-3 pr-2">
                      {/* Status & Category */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Status</label>
                          {canEdit ? (
                            <select
                              value={selectedItem.status || 'pending'}
                              onChange={(e) => onUpdateItem(selectedItem.id, { status: e.target.value })}
                              className="w-full mt-1 p-2 border rounded-md text-sm bg-background"
                            >
                              <option value="pending">Pending</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                            </select>
                          ) : (
                            <p className="mt-1 p-2 text-sm capitalize bg-slate-50 dark:bg-slate-800 rounded-md">
                              {selectedItem.status || 'Pending'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Hub</label>
                          {canEdit ? (
                            <select
                              value={selectedItem.hub || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { hub: e.target.value })}
                              className="w-full mt-1 p-2 border rounded-md text-sm bg-background"
                            >
                              <option value="">Select hub...</option>
                              <option value="Sales">Sales</option>
                              <option value="Operations">Operations</option>
                              <option value="Marketing">Marketing</option>
                              <option value="Finance">Finance</option>
                              <option value="Platform">Platform</option>
                            </select>
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md">
                              {selectedItem.hub || 'Not assigned'}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Description */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Description</label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Describe this feature..."
                            value={selectedItem.description || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { description: e.target.value })}
                            className="mt-1 min-h-[80px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[80px] whitespace-pre-wrap">
                            {selectedItem.description || 'No description'}
                          </p>
                        )}
                      </div>
                      
                      {/* Acceptance Criteria */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <ListChecks className="h-3 w-3 text-green-500" />
                          Acceptance Criteria
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="- [ ] Criterion 1&#10;- [ ] Criterion 2&#10;- [ ] Criterion 3"
                            value={selectedItem.acceptance_criteria || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { acceptance_criteria: e.target.value })}
                            className="mt-1 min-h-[80px] text-sm font-mono"
                          />
                        ) : (
                          <pre className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[60px] whitespace-pre-wrap font-mono">
                            {selectedItem.acceptance_criteria || 'No acceptance criteria defined'}
                          </pre>
                        )}
                      </div>
                      
                      {/* Progress */}
                      <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Progress</label>
                          <span className="text-sm font-bold text-slate-700 dark:text-slate-300">
                            {pendingProgress !== null ? pendingProgress : (selectedItem.progress || 0)}%
                          </span>
                        </div>
                        {canEdit && (
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="100"
                              value={pendingProgress !== null ? pendingProgress : (selectedItem.progress || 0)}
                              onChange={(e) => onSetPendingProgress(parseInt(e.target.value))}
                              className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-lg appearance-none cursor-pointer"
                            />
                            {pendingProgress !== null && pendingProgress !== (selectedItem.progress || 0) && (
                              <div className="flex items-center gap-1">
                                <button
                                  onClick={() => {
                                    onUpdateItem(selectedItem.id, { progress: pendingProgress });
                                    onSetPendingProgress(null);
                                  }}
                                  className="p-1 rounded-full bg-green-500 hover:bg-green-600 text-white"
                                  title="Save"
                                >
                                  <Check className="h-3 w-3" />
                                </button>
                                <button
                                  onClick={() => onSetPendingProgress(null)}
                                  className="p-1 rounded-full bg-slate-400 hover:bg-slate-500 text-white"
                                  title="Cancel"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                        <div className="mt-2 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-green-400 to-green-500 rounded-full transition-all"
                            style={{ width: `${pendingProgress !== null ? pendingProgress : (selectedItem.progress || 0)}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Delete Feature Button */}
                      {canEdit && (
                        <div className="pt-4 mt-4 border-t border-slate-200 dark:border-slate-700">
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button 
                                variant="destructive" 
                                size="sm" 
                                className="w-full"
                                data-testid="delete-feature-btn"
                              >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete Feature
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete "{selectedItem.name}"?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This action cannot be undone. This will permanently delete this feature 
                                  and all associated data including specifications, test cases, and progress tracking.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => onDeleteItem(selectedItem.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                  data-testid="confirm-delete-btn"
                                >
                                  Delete Forever
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      )}
                    </TabsContent>
                    
                    {/* Technical Tab */}
                    <TabsContent value="technical" className="mt-0 space-y-3 pr-2">
                      {/* Technical Requirements */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <FileCode className="h-3 w-3 text-blue-500" />
                          Technical Requirements
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Backend:&#10;- Endpoint: POST /api/...&#10;&#10;Frontend:&#10;- Component: ComponentName.js"
                            value={selectedItem.technical_requirements || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { technical_requirements: e.target.value })}
                            className="mt-1 min-h-[120px] text-sm font-mono"
                          />
                        ) : (
                          <pre className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[100px] whitespace-pre-wrap font-mono">
                            {selectedItem.technical_requirements || 'No technical requirements'}
                          </pre>
                        )}
                      </div>
                      
                      {/* Related Files */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <FileText className="h-3 w-3 text-purple-500" />
                          Related Files / Components
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="/app/backend/routes/...&#10;/app/frontend/src/pages/..."
                            value={(selectedItem.related_files || []).join('\n')}
                            onChange={(e) => onUpdateItem(selectedItem.id, { related_files: e.target.value.split('\n').filter(f => f.trim()) })}
                            className="mt-1 min-h-[80px] text-sm font-mono"
                          />
                        ) : (
                          <div className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded-md min-h-[60px]">
                            {selectedItem.related_files?.length > 0 ? (
                              selectedItem.related_files.map((file, idx) => (
                                <div key={idx} className="text-sm font-mono text-blue-600 dark:text-blue-400">{file}</div>
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground">No related files</span>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Dependencies */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 text-orange-500" />
                          Dependencies / Blockers
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="- Requires: existing feature/API&#10;- Blocked by: ..."
                            value={selectedItem.dependencies_notes || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { dependencies_notes: e.target.value })}
                            className="mt-1 min-h-[60px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                            {selectedItem.dependencies_notes || 'No dependencies noted'}
                          </p>
                        )}
                      </div>
                      
                      {/* Reference Links */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <ExternalLink className="h-3 w-3 text-cyan-500" />
                          Reference Links
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="https://figma.com/...&#10;https://docs.example.com/..."
                            value={(selectedItem.reference_links || []).join('\n')}
                            onChange={(e) => onUpdateItem(selectedItem.id, { reference_links: e.target.value.split('\n').filter(l => l.trim()) })}
                            className="mt-1 min-h-[60px] text-sm font-mono"
                          />
                        ) : (
                          <div className="mt-1 p-2 bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px]">
                            {selectedItem.reference_links?.length > 0 ? (
                              selectedItem.reference_links.map((link, idx) => (
                                <a key={idx} href={link} target="_blank" rel="noopener noreferrer" 
                                   className="text-sm text-blue-600 hover:underline block truncate">{link}</a>
                              ))
                            ) : (
                              <span className="text-sm text-muted-foreground">No reference links</span>
                            )}
                          </div>
                        )}
                      </div>
                    </TabsContent>
                    
                    {/* Testing Tab */}
                    <TabsContent value="testing" className="mt-0 space-y-3 pr-2">
                      {/* Testing Instructions */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <TestTube className="h-3 w-3 text-purple-500" />
                          Testing Instructions
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="1. Navigate to /page&#10;2. Click on 'Button'&#10;3. Enter data: ...&#10;4. Verify that..."
                            value={selectedItem.testing_instructions || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { testing_instructions: e.target.value })}
                            className="mt-1 min-h-[120px] text-sm font-mono"
                          />
                        ) : (
                          <pre className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[100px] whitespace-pre-wrap font-mono">
                            {selectedItem.testing_instructions || 'No testing instructions'}
                          </pre>
                        )}
                      </div>
                      
                      {/* Test Scenarios */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Test Scenarios / Edge Cases</label>
                        {canEdit ? (
                          <Textarea
                            placeholder="- Empty input validation&#10;- Large data handling&#10;- Error states"
                            value={selectedItem.test_scenarios || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { test_scenarios: e.target.value })}
                            className="mt-1 min-h-[80px] text-sm font-mono"
                          />
                        ) : (
                          <pre className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[60px] whitespace-pre-wrap font-mono">
                            {selectedItem.test_scenarios || 'No test scenarios defined'}
                          </pre>
                        )}
                      </div>
                      
                      {/* Success Indicators */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <CheckCircle className="h-3 w-3 text-green-500" />
                          Success Indicators
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="- Toast message shows 'Success'&#10;- Data appears in table&#10;- API returns 200 status"
                            value={selectedItem.success_indicators || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { success_indicators: e.target.value })}
                            className="mt-1 min-h-[60px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                            {selectedItem.success_indicators || 'No success indicators defined'}
                          </p>
                        )}
                      </div>
                    </TabsContent>
                    
                    {/* Setup Tab */}
                    <TabsContent value="setup" className="mt-0 space-y-3 pr-2">
                      {/* API Keys Needed */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <Key className="h-3 w-3 text-yellow-500" />
                          API Keys Needed
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="List required API keys (e.g., Stripe API Key, SendGrid API Key)..."
                            value={selectedItem.api_keys_needed || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { api_keys_needed: e.target.value })}
                            className="mt-1 min-h-[80px] text-sm font-mono"
                          />
                        ) : (
                          <pre className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[60px] whitespace-pre-wrap font-mono">
                            {selectedItem.api_keys_needed || 'No API keys specified'}
                          </pre>
                        )}
                      </div>
                      
                      {/* Environment Variables */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <Settings className="h-3 w-3 text-blue-500" />
                          Environment Variables
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="KEY_NAME=value&#10;ANOTHER_KEY=value"
                            value={selectedItem.env_variables || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { env_variables: e.target.value })}
                            className="mt-1 min-h-[80px] text-sm font-mono"
                          />
                        ) : (
                          <pre className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[60px] whitespace-pre-wrap font-mono">
                            {selectedItem.env_variables || 'No environment variables specified'}
                          </pre>
                        )}
                      </div>
                      
                      {/* Third Party Credentials */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <Lock className="h-3 w-3 text-red-500" />
                          Third Party Credentials
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Service accounts, OAuth tokens, or other credentials needed..."
                            value={selectedItem.third_party_credentials || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { third_party_credentials: e.target.value })}
                            className="mt-1 min-h-[60px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                            {selectedItem.third_party_credentials || 'No third party credentials specified'}
                          </p>
                        )}
                      </div>
                      
                      {/* Setup Instructions */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <ClipboardList className="h-3 w-3 text-green-500" />
                          Setup Instructions
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="1. Create account at...&#10;2. Generate API key...&#10;3. Add to .env file..."
                            value={selectedItem.setup_instructions || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { setup_instructions: e.target.value })}
                            className="mt-1 min-h-[100px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[80px] whitespace-pre-wrap">
                            {selectedItem.setup_instructions || 'No setup instructions specified'}
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground mt-1">Step-by-step setup guide before development can begin</p>
                      </div>
                      
                      {/* Config Notes */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Configuration Notes</label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Any additional configuration requirements or notes..."
                            value={selectedItem.config_notes || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { config_notes: e.target.value })}
                            className="mt-1 min-h-[60px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                            {selectedItem.config_notes || 'No configuration notes'}
                          </p>
                        )}
                      </div>
                    </TabsContent>
                    
                    {/* Security & Data Tab */}
                    <TabsContent value="security" className="mt-0 space-y-3 pr-2">
                      {/* Roles & Permissions */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <ShieldCheck className="h-3 w-3 text-purple-500" />
                          Roles & Permissions
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Which user roles can access this feature?&#10;e.g., admin, super_admin, sales_rep&#10;- Admins: full CRUD access&#10;- Users: read-only access"
                            value={selectedItem.roles_permissions || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { roles_permissions: e.target.value })}
                            className="mt-1 min-h-[70px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[50px] whitespace-pre-wrap">
                            {selectedItem.roles_permissions || 'No roles/permissions defined'}
                          </p>
                        )}
                      </div>
                      
                      {/* Data Access */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <FileText className="h-3 w-3 text-blue-500" />
                          Data Access Requirements
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="What data models/collections are read/written?&#10;e.g.,&#10;- users: read&#10;- orders: read, write&#10;- payments: write"
                            value={selectedItem.data_access || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { data_access: e.target.value })}
                            className="mt-1 min-h-[70px] text-sm font-mono"
                          />
                        ) : (
                          <pre className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[50px] whitespace-pre-wrap font-mono">
                            {selectedItem.data_access || 'No data access requirements defined'}
                          </pre>
                        )}
                      </div>
                      
                      {/* Auth & Authorization Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                            <Key className="h-3 w-3 text-yellow-500" />
                            Authentication
                          </label>
                          {canEdit ? (
                            <Textarea
                              placeholder="e.g., JWT required, session-based, API key, public"
                              value={selectedItem.auth_requirements || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { auth_requirements: e.target.value })}
                              className="mt-1 min-h-[60px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                              {selectedItem.auth_requirements || 'Not specified'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                            <Lock className="h-3 w-3 text-red-500" />
                            Authorization Rules
                          </label>
                          {canEdit ? (
                            <Textarea
                              placeholder="e.g., User must own resource, Admin bypass, Role-based"
                              value={selectedItem.authorization_rules || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { authorization_rules: e.target.value })}
                              className="mt-1 min-h-[60px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                              {selectedItem.authorization_rules || 'Not specified'}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Data Validation */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <ListChecks className="h-3 w-3 text-green-500" />
                          Data Validation Rules
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Input validation requirements:&#10;- Email: valid format&#10;- Phone: regex pattern&#10;- Text fields: max 500 chars&#10;- Required fields: name, email"
                            value={selectedItem.data_validation || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { data_validation: e.target.value })}
                            className="mt-1 min-h-[70px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[50px] whitespace-pre-wrap">
                            {selectedItem.data_validation || 'No validation rules defined'}
                          </p>
                        )}
                      </div>
                      
                      {/* PII & Privacy */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 text-orange-500" />
                          Privacy / PII Handling
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Sensitive data considerations:&#10;- Mask SSN in UI&#10;- Encrypt at rest&#10;- GDPR compliant deletion&#10;- No PII in logs"
                            value={selectedItem.pii_handling || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { pii_handling: e.target.value })}
                            className="mt-1 min-h-[60px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                            {selectedItem.pii_handling || 'No PII handling rules defined'}
                          </p>
                        )}
                      </div>
                      
                      {/* Rate Limiting & Audit Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Rate Limiting</label>
                          {canEdit ? (
                            <Textarea
                              placeholder="e.g., 100 req/min per user, 1000 req/min total"
                              value={selectedItem.rate_limiting || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { rate_limiting: e.target.value })}
                              className="mt-1 min-h-[50px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[30px] whitespace-pre-wrap">
                              {selectedItem.rate_limiting || 'Not specified'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Audit Logging</label>
                          {canEdit ? (
                            <Textarea
                              placeholder="e.g., Log CRUD operations, user ID, timestamp, IP"
                              value={selectedItem.audit_logging || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { audit_logging: e.target.value })}
                              className="mt-1 min-h-[50px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[30px] whitespace-pre-wrap">
                              {selectedItem.audit_logging || 'Not specified'}
                            </p>
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* UI/UX Specs Tab */}
                    <TabsContent value="uiux" className="mt-0 space-y-3 pr-2">
                      {/* Wireframe Links */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <Layout className="h-3 w-3 text-indigo-500" />
                          Wireframe / Mockup Links
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Links to Figma, mockups, or wireframe references...&#10;e.g., https://figma.com/file/xyz"
                            value={selectedItem.wireframe_links || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { wireframe_links: e.target.value })}
                            className="mt-1 min-h-[60px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                            {selectedItem.wireframe_links || 'No wireframes specified'}
                          </p>
                        )}
                      </div>
                      
                      {/* UI Components */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <Layers className="h-3 w-3 text-blue-500" />
                          UI Components to Use
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Which Shadcn/UI components to use:&#10;- Dialog for modals&#10;- Table for data lists&#10;- Form with validation&#10;- Tabs for sections"
                            value={selectedItem.ui_components || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { ui_components: e.target.value })}
                            className="mt-1 min-h-[70px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[50px] whitespace-pre-wrap">
                            {selectedItem.ui_components || 'No components specified'}
                          </p>
                        )}
                      </div>
                      
                      {/* Responsive & Accessibility Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                            <Smartphone className="h-3 w-3 text-green-500" />
                            Responsive Requirements
                          </label>
                          {canEdit ? (
                            <Textarea
                              placeholder="Mobile/tablet breakpoints:&#10;- Mobile: stack columns&#10;- Tablet: 2-col grid&#10;- Desktop: full layout"
                              value={selectedItem.responsive_requirements || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { responsive_requirements: e.target.value })}
                              className="mt-1 min-h-[60px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                              {selectedItem.responsive_requirements || 'Not specified'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                            <Accessibility className="h-3 w-3 text-purple-500" />
                            Accessibility Level
                          </label>
                          {canEdit ? (
                            <Textarea
                              placeholder="WCAG compliance level:&#10;- WCAG 2.1 AA&#10;- Keyboard navigation&#10;- Screen reader support&#10;- Color contrast 4.5:1"
                              value={selectedItem.accessibility_level || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { accessibility_level: e.target.value })}
                              className="mt-1 min-h-[60px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                              {selectedItem.accessibility_level || 'Not specified'}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* User Flow */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <ArrowRight className="h-3 w-3 text-cyan-500" />
                          User Flow Description
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Step-by-step user journey:&#10;1. User clicks 'Create' button&#10;2. Modal opens with form&#10;3. User fills required fields&#10;4. Submit shows loading state&#10;5. Success toast and redirect"
                            value={selectedItem.user_flow || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { user_flow: e.target.value })}
                            className="mt-1 min-h-[80px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[60px] whitespace-pre-wrap">
                            {selectedItem.user_flow || 'No user flow defined'}
                          </p>
                        )}
                      </div>
                      
                      {/* States Grid */}
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Error States</label>
                          {canEdit ? (
                            <Textarea
                              placeholder="Toast, inline, modal..."
                              value={selectedItem.error_states_ui || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { error_states_ui: e.target.value })}
                              className="mt-1 min-h-[50px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[30px] whitespace-pre-wrap text-xs">
                              {selectedItem.error_states_ui || 'N/A'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Loading States</label>
                          {canEdit ? (
                            <Textarea
                              placeholder="Skeleton, spinner..."
                              value={selectedItem.loading_states || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { loading_states: e.target.value })}
                              className="mt-1 min-h-[50px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[30px] whitespace-pre-wrap text-xs">
                              {selectedItem.loading_states || 'N/A'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Empty States</label>
                          {canEdit ? (
                            <Textarea
                              placeholder="Placeholder text, CTA..."
                              value={selectedItem.empty_states || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { empty_states: e.target.value })}
                              className="mt-1 min-h-[50px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[30px] whitespace-pre-wrap text-xs">
                              {selectedItem.empty_states || 'N/A'}
                            </p>
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* Error Handling Tab */}
                    <TabsContent value="errors" className="mt-0 space-y-3 pr-2">
                      {/* Error Scenarios */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <Bug className="h-3 w-3 text-red-500" />
                          Expected Error Scenarios
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="What can go wrong?&#10;- Network timeout&#10;- Invalid input data&#10;- Resource not found&#10;- Permission denied&#10;- Rate limit exceeded"
                            value={selectedItem.error_scenarios || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { error_scenarios: e.target.value })}
                            className="mt-1 min-h-[80px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[60px] whitespace-pre-wrap">
                            {selectedItem.error_scenarios || 'No error scenarios defined'}
                          </p>
                        )}
                      </div>
                      
                      {/* Error Messages */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <XCircle className="h-3 w-3 text-orange-500" />
                          User-Facing Error Messages
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="Error message text for each scenario:&#10;- 'Unable to save. Please try again.'&#10;- 'Session expired. Please log in.'&#10;- 'You don't have permission to do this.'"
                            value={selectedItem.error_messages || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { error_messages: e.target.value })}
                            className="mt-1 min-h-[70px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[50px] whitespace-pre-wrap">
                            {selectedItem.error_messages || 'No error messages defined'}
                          </p>
                        )}
                      </div>
                      
                      {/* Fallback & Retry Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                            <RefreshCw className="h-3 w-3 text-blue-500" />
                            Retry Logic
                          </label>
                          {canEdit ? (
                            <Textarea
                              placeholder="Auto-retry requirements:&#10;- 3 retries max&#10;- Exponential backoff&#10;- 1s, 2s, 4s delays"
                              value={selectedItem.retry_logic || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { retry_logic: e.target.value })}
                              className="mt-1 min-h-[60px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                              {selectedItem.retry_logic || 'Not specified'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                            <Timer className="h-3 w-3 text-yellow-500" />
                            Timeout Handling
                          </label>
                          {canEdit ? (
                            <Textarea
                              placeholder="Timeout durations:&#10;- API calls: 30s&#10;- File uploads: 120s&#10;- Show timeout error after"
                              value={selectedItem.timeout_handling || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { timeout_handling: e.target.value })}
                              className="mt-1 min-h-[60px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                              {selectedItem.timeout_handling || 'Not specified'}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Fallback Behaviors */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400 flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3 text-amber-500" />
                          Fallback Behaviors
                        </label>
                        {canEdit ? (
                          <Textarea
                            placeholder="What to do when things fail:&#10;- Show cached data if API fails&#10;- Disable feature gracefully&#10;- Redirect to error page&#10;- Offer offline mode"
                            value={selectedItem.fallback_behaviors || ''}
                            onChange={(e) => onUpdateItem(selectedItem.id, { fallback_behaviors: e.target.value })}
                            className="mt-1 min-h-[70px] text-sm"
                          />
                        ) : (
                          <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[50px] whitespace-pre-wrap">
                            {selectedItem.fallback_behaviors || 'No fallback behaviors defined'}
                          </p>
                        )}
                      </div>
                      
                      {/* Logging & Graceful Degradation Grid */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Logging Requirements</label>
                          {canEdit ? (
                            <Textarea
                              placeholder="What to log:&#10;- Request/response&#10;- Stack traces&#10;- User context"
                              value={selectedItem.logging_requirements || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { logging_requirements: e.target.value })}
                              className="mt-1 min-h-[60px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                              {selectedItem.logging_requirements || 'Not specified'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Graceful Degradation</label>
                          {canEdit ? (
                            <Textarea
                              placeholder="How feature degrades:&#10;- If payment fails: queue&#10;- If AI fails: manual mode&#10;- If sync fails: local cache"
                              value={selectedItem.graceful_degradation || ''}
                              onChange={(e) => onUpdateItem(selectedItem.id, { graceful_degradation: e.target.value })}
                              className="mt-1 min-h-[60px] text-sm"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md min-h-[40px] whitespace-pre-wrap">
                              {selectedItem.graceful_degradation || 'Not specified'}
                            </p>
                          )}
                        </div>
                      </div>
                    </TabsContent>
                    
                    {/* Schedule Tab */}
                    <TabsContent value="schedule" className="mt-0 space-y-3 pr-2">
                      {/* Dates */}
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Start Date</label>
                          {canEdit ? (
                            <Input
                              type="date"
                              defaultValue={selectedItem.start_date || ''}
                              key={`start-${selectedItem.id}-${selectedItem.start_date}`}
                              onBlur={(e) => {
                                if (e.target.value !== selectedItem.start_date) {
                                  onUpdateItem(selectedItem.id, { start_date: e.target.value });
                                }
                              }}
                              className="mt-1"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md">
                              {selectedItem.start_date || 'Not set'}
                            </p>
                          )}
                        </div>
                        <div>
                          <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Target Date</label>
                          {canEdit ? (
                            <Input
                              type="date"
                              defaultValue={selectedItem.target_date || ''}
                              key={`target-${selectedItem.id}-${selectedItem.target_date}`}
                              onBlur={(e) => {
                                if (e.target.value !== selectedItem.target_date) {
                                  onUpdateItem(selectedItem.id, { target_date: e.target.value });
                                }
                              }}
                              className="mt-1"
                            />
                          ) : (
                            <p className="mt-1 p-2 text-sm bg-slate-50 dark:bg-slate-800 rounded-md">
                              {selectedItem.target_date || 'Not set'}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      {/* Time Tracking */}
                      <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <h5 className="text-xs font-medium text-blue-700 dark:text-blue-300 mb-2">Time Tracking</h5>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="text-[10px] text-slate-600 dark:text-slate-400">Projected Hours</label>
                            {canEdit ? (
                              <Input
                                type="number"
                                min="0"
                                step="0.5"
                                placeholder="0"
                                value={selectedItem.projected_hours || ''}
                                onChange={(e) => onUpdateItem(selectedItem.id, { projected_hours: parseFloat(e.target.value) || 0 })}
                                className="mt-1 h-8"
                              />
                            ) : (
                              <p className="mt-1 p-1 text-sm bg-white dark:bg-slate-700 rounded-md h-8 flex items-center">
                                {selectedItem.projected_hours || 0}h
                              </p>
                            )}
                          </div>
                          <div>
                            <label className="text-[10px] text-slate-600 dark:text-slate-400">Actual Hours</label>
                            {canEdit ? (
                              <Input
                                type="number"
                                min="0"
                                step="0.5"
                                placeholder="0"
                                value={selectedItem.actual_hours || ''}
                                onChange={(e) => onUpdateItem(selectedItem.id, { actual_hours: parseFloat(e.target.value) || 0 })}
                                className="mt-1 h-8"
                              />
                            ) : (
                              <p className="mt-1 p-1 text-sm bg-white dark:bg-slate-700 rounded-md h-8 flex items-center">
                                {selectedItem.actual_hours || 0}h
                              </p>
                            )}
                          </div>
                        </div>
                        {selectedItem.projected_hours > 0 && (
                          <div className="mt-2 text-[10px] text-blue-600 dark:text-blue-400">
                            Remaining: {Math.max(0, (selectedItem.projected_hours || 0) - (selectedItem.actual_hours || 0))}h
                          </div>
                        )}
                      </div>
                      
                      {/* Category */}
                      <div>
                        <label className="text-xs font-medium text-slate-600 dark:text-slate-400">Category</label>
                        {canEdit ? (
                          <select
                            value={selectedItem.category || 'mvp'}
                            onChange={(e) => onUpdateItem(selectedItem.id, { category: e.target.value })}
                            className="w-full mt-1 p-2 border rounded-md text-sm bg-background"
                          >
                            <option value="mvp">MVP</option>
                            <option value="future">Future</option>
                          </select>
                        ) : (
                          <p className="mt-1 p-2 text-sm uppercase bg-slate-50 dark:bg-slate-800 rounded-md">
                            {selectedItem.category || 'MVP'}
                          </p>
                        )}
                      </div>
                      
                      {/* Linked Items Info */}
                      <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                        <h5 className="text-xs font-medium text-slate-600 dark:text-slate-400 mb-2">Linked Items</h5>
                        <div className="space-y-1 text-[10px]">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-3 w-3 text-blue-500" />
                            <span>Timeline: {selectedItem.timeline_id ? 'Linked' : 'Not linked'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <FileText className="h-3 w-3 text-purple-500" />
                            <span>Implementation: {selectedItem.implementation_id ? 'Linked' : 'Not linked'}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <ShieldCheck className="h-3 w-3 text-cyan-500" />
                            <span>Data & Security: {selectedItem.data_security_id ? 'Linked' : 'Not linked'}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Delete Button */}
                      {canEdit && (
                        <Button
                          variant="destructive"
                          size="sm"
                          className="w-full"
                          onClick={() => {
                            onDeleteItem(selectedItem.id);
                            onSelectItem(null);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Feature
                        </Button>
                      )}
                    </TabsContent>
                  </ScrollArea>
                </Tabs>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default EnhancedScopeModal;
